import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Grid,
  LinearProgress,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import type { Risk, Component } from 'shared';

// =============================================================================
// TYPES
// =============================================================================

interface RiskPreviewModalProps {
  open: boolean;
  onClose: () => void;
  risk: Risk | null;
  component?: Component;
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface ScoreChipProps {
  score: number | string | undefined;
  showLabel?: boolean;
}

const RiskLevelChip: React.FC<ScoreChipProps> = ({
  score,
  showLabel = true,
}) => {
  const { t } = useTranslation();
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score)) || 0;

  const getRiskLevel = (
    s: number
  ): { label: string; color: string; bgColor: string } => {
    if (s >= 8)
      return {
        label: t('risk.level.critical'),
        color: '#FE3B00',
        bgColor: '#FE3B0020',
      };
    if (s >= 6)
      return {
        label: t('risk.level.high'),
        color: '#2D3B45',
        bgColor: '#2D3B4520',
      };
    if (s >= 4)
      return {
        label: t('risk.level.medium'),
        color: '#FFBB00',
        bgColor: '#FFBB0020',
      };
    if (s >= 2)
      return {
        label: t('risk.level.low'),
        color: '#42B8B2',
        bgColor: '#42B8B220',
      };
    return {
      label: t('risk.level.minimal'),
      color: '#939597',
      bgColor: '#93959720',
    };
  };

  const { label, color, bgColor } = getRiskLevel(numScore);

  return (
    <Chip
      label={
        showLabel ? `${numScore.toFixed(1)} - ${label}` : numScore.toFixed(1)
      }
      size="small"
      sx={{
        backgroundColor: bgColor,
        color: color,
        fontWeight: 600,
      }}
    />
  );
};

interface CIAScoreBarProps {
  label: string;
  score: number | string | undefined;
  color: string;
}

const CIAScoreBar: React.FC<CIAScoreBarProps> = ({ label, score, color }) => {
  const numScore =
    typeof score === 'number' ? score : parseFloat(String(score)) || 0;
  const percentage = (numScore / 10) * 100;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={600} sx={{ color }}>
          {numScore.toFixed(1)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: `${color}20`,
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * RiskPreviewModal - Quick preview of risk details
 *
 * Shows essential risk information for quick reference while working in diagrams.
 * For full details, users can click "View Full Details" to navigate to the
 * dedicated RiskDetail page.
 */
export const RiskPreviewModal: React.FC<RiskPreviewModalProps> = ({
  open,
  onClose,
  risk,
  component,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!risk) return null;

  const handleViewFullDetails = () => {
    onClose();
    navigate(`/risks/${risk.id}`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '80vh' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}
      >
        <WarningIcon sx={{ color: '#2D3B45' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {risk.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Chip
              label={t(`risk.status.${risk.status}`)}
              size="small"
              variant="outlined"
              color={
                risk.status === 'closed' || risk.status === 'mitigated'
                  ? 'success'
                  : risk.status === 'identified'
                    ? 'warning'
                    : 'default'
              }
            />
            <Chip
              label={t(`risk.treatment.${risk.riskTreatment}`)}
              size="small"
              sx={{
                backgroundColor:
                  risk.riskTreatment === 'mitigate'
                    ? '#42B8B220'
                    : risk.riskTreatment === 'accept'
                      ? '#FFBB0020'
                      : '#2D3B4520',
                color:
                  risk.riskTreatment === 'mitigate'
                    ? '#42B8B2'
                    : risk.riskTreatment === 'accept'
                      ? '#FFBB00'
                      : '#2D3B45',
              }}
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Description */}
        {risk.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {risk.description.length > 200
              ? `${risk.description.substring(0, 200)}...`
              : risk.description}
          </Typography>
        )}

        {/* Component Reference */}
        {component && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('risk.fields.component')}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {component.name} (
              {t(`componentTypes:${component.type}`, component.type)})
            </Typography>
          </Box>
        )}

        {/* Risk Scores */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('risk.fields.inherentRisk')}
                </Typography>
                <RiskLevelChip score={risk.inherentRisk} />
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {t('risk.fields.residualRisk')}
                </Typography>
                <RiskLevelChip score={risk.residualRisk} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* CIA Impact Summary */}
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <ShieldIcon sx={{ fontSize: 16 }} />
          {t('risk.detail.ciaImpact')}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <CIAScoreBar
            label={t('classification.confidentiality.title')}
            score={risk.confidentialityImpact}
            color="#2D3B45"
          />
          <CIAScoreBar
            label={t('classification.integrity.title')}
            score={risk.integrityImpact}
            color="#42B8B2"
          />
          <CIAScoreBar
            label={t('classification.availability.title')}
            score={risk.availabilityImpact}
            color="#FFBB00"
          />
        </Box>

        {/* Threat Summary */}
        {(risk.threatSource || risk.vulnerability) && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <SecurityIcon sx={{ fontSize: 16 }} />
              {t('risk.detail.threatAnalysis')}
            </Typography>
            {risk.threatSource && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                <strong>{t('risk.fields.threatSource')}:</strong>{' '}
                {risk.threatSource}
              </Typography>
            )}
            {risk.vulnerability && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('risk.fields.vulnerability')}:</strong>{' '}
                {risk.vulnerability}
              </Typography>
            )}
          </Box>
        )}

        {/* Owner & Review Date */}
        <Grid container spacing={2}>
          {risk.owner && (
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">
                {t('risk.fields.owner')}
              </Typography>
              <Typography variant="body2">{risk.owner}</Typography>
            </Grid>
          )}
          {risk.reviewDate && (
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">
                {t('risk.fields.reviewDate')}
              </Typography>
              <Typography variant="body2">
                {new Date(risk.reviewDate).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t('common.close')}
        </Button>
        <Button
          variant="contained"
          endIcon={<OpenInNewIcon />}
          onClick={handleViewFullDetails}
        >
          {t('risk.detail.viewFullDetails')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Keep the old export name for backward compatibility
export { RiskPreviewModal as RiskDetailModal };
